import { action, extendObservable } from 'mobx';
import FetchService from '../../../core/services/FetchService';
import FormService from '../../../core/services/FormService';
import ConfirmationDialogService from '../../../core/services/ConfirmationDialogService';
import NotificationService from '../../../core/services/NotificationService';
import { browserHistory } from 'react-router';
import { head, orderBy } from 'lodash';
import moment from 'moment/moment';

class ContractListService {
  fetch = new FetchService();
  submit = new FetchService();
  form = new FormService();

  constructor() {
    extendObservable(this, {
      contracts: [],
      pagination: {
        current: 1,
        pageSize: 15,
        total: 0,
      },
    });
  }

  init = action((status) => {
    this.form.reset();
    this.status = status;
    this.pagination = {
      ...this.pagination,
      current: 1,
    };
  });

  load = action(() => {
    this.fetch
      .fetch({
        url: '/contracts',
        apiVersion: 'v2',
        query: {
          ...{
            ...(this.form.getValue('documentNumber') && {
              documentNumber: this.form.getValue('documentNumber'),
            }),
            ...(this.form.getValue('socialReason') && {
              socialReason: this.form.getValue('socialReason'),
            }),
            ...(this.form.getValue('segment') && {
              segment: this.form.getValue('segment'),
            }),
            ...(this.form.getValue('company') && { company: this.form.getValue('company') }),
            ...(this.form.getValue('distributor') && { distributor: this.form.getValue('distributor') }),
          },
          status: this.status,
          page: this.pagination.current,
          size: this.pagination.pageSize,
        },
      })
      .then(() => {
        if (this.fetch.data) {
          this.contracts = this.fetch.data.docs;
          this.pagination = { ...this.pagination, total: this.fetch.data.total };
        } else {
          this.contracts = [];
          this.pagination = { ...this.pagination, total: 0 };
        }
      });
  });

  handleAction = (action, contract) => {
    switch (action) {
      case 'cancel':
        this.handleCancel(contract);
        break;
      case 'upgrade':
        this.handleUpgrade(contract);
        break;
      case 'downgrade':
        this.handleDowngrade(contract);
        break;
      case 'delete':
        this.handleDelete(contract);
        break;
      case 'anticipate-activation':
        this.handleAnticipateActivation(contract);
        break;
      case 'block':
        this.handleBlock(contract);
        break;
      case 'unblock':
        this.handleUnblock(contract);
        break;
      case 'force-reactivation':
        this.handleForceReactivation(contract);
        break;
      case 'remove-credit-card':
        this.handleRemoveCreditCard(contract);
        break;
      default:
        break;
    }
  };

  handleUpgrade = (contract) => {
    this.fetch
      .fetch({
        url: `/contracts/${contract.id}/upgrade`,
        method: 'head',
        apiVersion: 'v2',
      })
      .then(() => {
        if (this.fetch.response.status === 400) {
          NotificationService.addNotification(`Contract has pending bills and can't be upgraded.`, 'error');
        } else {
          browserHistory.push({
            pathname: `/contracts/${contract.id}/upgrade`,
            state: { from: { text: 'Contract', pathname: '/contracts' } },
          });
        }
      });
  };

  handleDowngrade = (contract) => {
    this.fetch
      .fetch({
        url: `/contracts/${contract.id}/downgrade`,
        method: 'head',
        apiVersion: 'v2',
      })
      .then(() => {
        if (this.fetch.response.status === 400) {
          NotificationService.addNotification(`Contract has pending bills and can't be downgraded.`, 'error');
        } else {
          browserHistory.push({
            pathname: `/contracts/${contract.id}/downgrade`,
            state: { from: { text: 'Contract', pathname: '/contracts' } },
          });
        }
      });
  };

  handleCancel = (contract) => {
    ConfirmationDialogService.show(
      'Cancel Contract',
      `Are you sure you want to cancel the contract ${contract.documentNumber}? This action will disable all access from contract's distributor or contract's company, including admin users and students. If you would like, you can activate the contract again.`,
      () => {
        return new Promise((resolve) => {
          this.submit
            .fetch({
              method: 'put',
              url: `/contracts/${contract.id}/cancel`,
              apiVersion: 'v2',
            })
            .then(() => {
              if (this.submit.data) {
                this.submit
                  .fetch({
                    method: 'get',
                    url: `/bills?page=1&documentNumber=${contract.documentNumber}`,
                    apiVersion: 'v2',
                  })
                  .then(() => {
                    NotificationService.addNotification(
                      `Contract ${contract.documentNumber} cancellation request successfully completed. The contract will be canceled in a few minutes.`,
                      'success'
                    );
                    if (this.submit.data.total > 0) {
                      const lastBill = head(orderBy(this.submit.data.docs, ['createdAt'], 'desc'));
                      const isOpenBill = lastBill.status === 'OPEN';
                      const isCreatedToday = moment(lastBill.createdAt).isSameOrAfter(moment().startOf('day'));
                      if (isOpenBill && isCreatedToday) {
                        browserHistory.push(`billing/${lastBill.id}/details`);
                      } else {
                        browserHistory.push('/contracts');
                      }
                    } else {
                      browserHistory.push('/contracts');
                    }
                    if (this.submit.error) {
                      browserHistory.push('/contracts');
                    }
                  });
                this.load();
              }
              if (this.submit.error) {
                NotificationService.addNotification(`Error cancelling contract.`, 'error');
              }
              resolve();
            });
        });
      }
    );
  };

  handleDelete = (contract) => {
    ConfirmationDialogService.show(
      'Delete Contract',
      `Are you sure you want to delete the contract ${contract.documentNumber}?`,
      () => {
        return new Promise((resolve) => {
          this.submit
            .fetch({
              method: 'delete',
              url: `/contracts/${contract.id}`,
              apiVersion: 'v2',
            })
            .then(() => {
              if (this.submit.data) {
                NotificationService.addNotification(
                  `Contract ${contract.documentNumber} successfully deleted.`,
                  'success'
                );
                this.load();
              }
              if (this.submit.error) {
                NotificationService.addNotification(`Error deleting contract.`, 'error');
              }
              resolve();
            });
        });
      }
    );
  };

  handleAnticipateActivation = (contract) => {
    ConfirmationDialogService.show(
      'Anticipate Contract Activation',
      `Are you sure you want to anticipate the contract ${contract.documentNumber} activation? The billing cycle will be adjusted to start today.`,
      () => {
        return new Promise((resolve) => {
          this.submit
            .fetch({
              method: 'patch',
              url: `/contracts/${contract.id}/anticipate-activation`,
              apiVersion: 'v3',
            })
            .then(() => {
              if (this.submit.data) {
                NotificationService.addNotification(
                  `Contract ${contract.documentNumber} successfully activated.`,
                  'success'
                );
                this.load();
              }
              if (this.submit.error) {
                NotificationService.addNotification(`Error activating contract.`, 'error');
              }
              resolve();
            });
        });
      }
    );
  };

  handleBlock = (contract) => {
    ConfirmationDialogService.show(
      'Block Contract',
      `Are you sure you want to block the contract ${contract.documentNumber}?`,
      () => {
        return new Promise((resolve) => {
          this.submit
            .fetch({
              method: 'patch',
              url: `/contracts/${contract.id}/block`,
              apiVersion: 'v3',
            })
            .then(() => {
              if (this.submit.data) {
                NotificationService.addNotification(
                  `Contract ${contract.documentNumber} successfully blocked.`,
                  'success'
                );
                this.load();
              }
              if (this.submit.error) {
                NotificationService.addNotification(`Error on block contract.`, 'error');
              }
              resolve();
            });
        });
      }
    );
  };

  handleUnblock = (contract) => {
    ConfirmationDialogService.show(
      'Unblock Contract',
      `Are you sure you want to unblock the contract ${contract.documentNumber}?`,
      () => {
        return new Promise((resolve) => {
          this.submit
            .fetch({
              method: 'patch',
              url: `/contracts/${contract.id}/unblock`,
              apiVersion: 'v3',
            })
            .then(() => {
              if (this.submit.data) {
                NotificationService.addNotification(
                  `Contract ${contract.documentNumber} successfully unblocked.`,
                  'success'
                );
                this.load();
              }
              if (this.submit.error) {
                NotificationService.addNotification(`Error on unblock contract.`, 'error');
              }
              resolve();
            });
        });
      }
    );
  };

  handleForceReactivation = (contract) => {
    ConfirmationDialogService.show(
      'Force Contract Reactivation',
      `Are you sure you want to force the contract reactivation? (DocNumber: ${contract.documentNumber})`,
      () => {
        return new Promise((resolve) => {
          this.submit
            .fetch({
              method: 'patch',
              url: `/contracts/${contract.id}/force-reactivate`,
              apiVersion: 'v3',
            })
            .then(() => {
              if (this.submit.data) {
                NotificationService.addNotification(
                  `Contract ${contract.documentNumber} successfully reactivated.`,
                  'success'
                );
                this.load();
              }
              if (this.submit.error) {
                NotificationService.addNotification(`Error reactivating contract.`, 'error');
              }
              resolve();
            });
        });
      }
    );
  };

  handleRemoveCreditCard = (contract) => {
    ConfirmationDialogService.show(
      'Remove Contract Credit Card',
      `Are you sure you want to remove the contract credit card payment method? (DocNumber: ${contract.documentNumber})`,
      () => {
        return new Promise((resolve) => {
          this.submit
            .fetch({
              method: 'delete',
              url: `/contracts/${contract.id}/payment-method/credit-card`,
              apiVersion: 'v3',
            })
            .then(() => {
              if (this.submit.data) {
                NotificationService.addNotification(
                  `Credit card payment method of contract ${contract.documentNumber} successfully removed.`,
                  'success'
                );
                this.load();
              }
              if (this.submit.error) {
                NotificationService.addNotification(`Error removing payment method.`, 'error');
              }
              resolve();
            });
        });
      }
    );
  };

  handleChange = action((pagination) => {
    this.pagination = { ...this.pagination, ...pagination };
    this.load();
  });
}

const contractListService = new ContractListService();

export default contractListService;
